
import { useState, useEffect } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface AdminToken {
  id: string; label: string; tokenPreview: string; isActive: boolean; lastUsedAt: string | null; createdAt: string;
}

export default function TokensPage() {
  const [tokens, setTokens] = useState<AdminToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState('');
  const [copied, setCopied] = useState(false);
  const [currentTokenSuffix, setCurrentTokenSuffix] = useState('');

  useEffect(() => {
    const t = typeof window !== 'undefined' ? sessionStorage.getItem('admin_token') : null;
    if (t) setCurrentTokenSuffix(t.slice(-8));
    loadTokens();
  }, []);

  const isCurrent = (t: AdminToken) => !!currentTokenSuffix && t.tokenPreview.endsWith(currentTokenSuffix);

  async function loadTokens() {
    try { setTokens(await adminHttpClient.get<AdminToken[]>(getAdminEndpointUrl('tokens', 'list'))); }
    catch (err) { console.error('Error cargando tokens:', err); }
    finally { setLoading(false); }
  }

  async function handleCreate() {
    if (!newLabel.trim()) return;
    setActionLoading('create');
    try {
      const result = await adminHttpClient.post<{ token: string }>(getAdminEndpointUrl('tokens', 'create'), { label: newLabel.trim() });
      setCreatedToken(result.token); setNewLabel('');
      await loadTokens();
    } catch (err) { console.error('Error creando token:', err); }
    finally { setActionLoading(''); }
  }

  async function handleToggle(id: string) {
    try { await adminHttpClient.post(getAdminEndpointUrl('tokens', 'toggle', { id })); await loadTokens(); }
    catch (err) { console.error('Error actualizando token:', err); }
  }

  async function handleDelete(id: string) {
    setActionLoading('delete');
    try { await adminHttpClient.delete(getAdminEndpointUrl('tokens', 'delete', { id })); setDeleteDialogOpen(null); await loadTokens(); }
    catch (err) { console.error('Error eliminando token:', err); }
    finally { setActionLoading(''); }
  }

  return (
    <AdminLayout
      title="Tokens de Acceso"
      subtitle={`${tokens.filter(t => t.isActive).length} activo${tokens.filter(t => t.isActive).length !== 1 ? 's' : ''}`}
      actions={
        <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-500 h-9" onClick={() => { setCreateDialogOpen(true); setCreatedToken(null); setNewLabel(''); }}>
          <span className="material-symbols-outlined text-[16px]">add</span> Crear Token
        </Button>
      }
    >
      <div className="space-y-5">
        {loading ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-12">
            <div className="flex justify-center"><span className="material-symbols-outlined text-3xl text-blue-500 animate-spin">progress_activity</span></div>
          </div>
        ) : tokens.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 mx-auto flex items-center justify-center mb-4">
              <span className="material-symbols-outlined text-3xl text-slate-400">key_off</span>
            </div>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">No hay tokens registrados</p>
            <p className="text-xs text-slate-400 mt-1">Crea tu primer token para acceder al panel</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {tokens.map((t) => (
              <div key={t.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-5 flex items-center justify-between group hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.isActive ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-slate-100 dark:bg-slate-800'}`}>
                    <span className={`material-symbols-outlined text-[20px] ${t.isActive ? 'text-blue-500' : 'text-slate-400'}`}>key</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">{t.label}</span>
                      {!t.isActive && <span className="text-[10px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">Inactivo</span>}
                      {isCurrent(t) && <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 px-1.5 py-0.5 rounded">En uso</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs font-mono text-slate-400">{t.tokenPreview}</span>
                      <span className="text-[11px] text-slate-400">
                        Usado: {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString('es-CL') : 'Nunca'}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        Creado: {new Date(t.createdAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch checked={t.isActive} disabled={isCurrent(t)} onCheckedChange={() => handleToggle(t.id)} />
                  </div>
                  <button
                    onClick={() => !isCurrent(t) && setDeleteDialogOpen(t.id)}
                    disabled={isCurrent(t)}
                    title={isCurrent(t) ? 'No puedes eliminar el token con el que estas autenticado' : 'Eliminar token'}
                    className="p-2 rounded-lg text-slate-400 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:bg-transparent"
                  >
                    <span className="material-symbols-outlined text-[18px]">{isCurrent(t) ? 'lock' : 'delete'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create dialog */}
        <Dialog open={createDialogOpen} onOpenChange={(open) => { if (!open) { setCreateDialogOpen(false); setCreatedToken(null); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="material-symbols-outlined text-blue-500 text-[20px]">add_circle</span>
                {createdToken ? 'Token Creado' : 'Crear Nuevo Token'}
              </DialogTitle>
            </DialogHeader>
            {createdToken ? (
              <div className="py-3 space-y-4">
                <div className="flex items-start gap-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <span className="material-symbols-outlined text-amber-600 text-[18px] mt-0.5">warning</span>
                  <p className="text-sm text-amber-800 dark:text-amber-300">Este token no se mostrara nuevamente. Copialo ahora.</p>
                </div>
                <div className="bg-slate-900 dark:bg-slate-800 rounded-lg p-4 font-mono text-sm text-emerald-400 break-all select-all leading-relaxed border border-slate-700">
                  {createdToken}
                </div>
                <Button
                  onClick={() => { navigator.clipboard.writeText(createdToken); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  variant="outline" className="w-full gap-1.5 h-10"
                >
                  <span className="material-symbols-outlined text-[16px]">{copied ? 'check' : 'content_copy'}</span>
                  {copied ? 'Copiado!' : 'Copiar Token'}
                </Button>
              </div>
            ) : (
              <>
                <div className="py-3">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Nombre del token</label>
                  <Input placeholder="Ej: Token de Humberto, CI/CD Pipeline..." value={newLabel} onChange={(e) => setNewLabel(e.target.value)} autoFocus className="h-10" />
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
                  <Button disabled={!newLabel.trim() || actionLoading === 'create'} onClick={handleCreate} className="gap-1.5 bg-blue-600 hover:bg-blue-500">
                    {actionLoading === 'create' ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>Creando...</> : 'Crear Token'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete dialog */}
        <Dialog open={!!deleteDialogOpen} onOpenChange={() => setDeleteDialogOpen(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="material-symbols-outlined text-red-500 text-[20px]">warning</span>
                Eliminar Token
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 dark:text-slate-400 py-1">
              Esta accion es irreversible. El token dejara de funcionar inmediatamente.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(null)}>Cancelar</Button>
              <Button variant="destructive" disabled={actionLoading === 'delete'} onClick={() => deleteDialogOpen && handleDelete(deleteDialogOpen)} className="gap-1.5">
                {actionLoading === 'delete' ? <><span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>Eliminando...</> : 'Eliminar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
